import { ReactNode, useCallback, useEffect, useRef } from 'react';

import { useDrawingShapeContext } from './drawingShapeContext';

import { useOptionalItems } from '~/api/easy-estimator-api/optional-items-api/useOptionalItems';
import { useSelectedStore } from '~/api/easy-estimator-api/stores-api/useSelectedStore';
import {
  GenericProduct,
  getEEProductType,
  isWarrantyProduct,
} from '~/core/helpers';
import useResetableState from '~/core/hooks/useResetableState';
import {
  OptionalProduct,
  ProductDetails,
  ProductNote,
  ProductQty,
  SelectedItemMap,
  SelectedItemProperties,
} from '~/core/models';
import {
  buildQteAttributes,
  createTypeSafeContext,
  getProductDescription,
  productQtyFromSelectedItems,
} from '~/core/utils';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { selectSelectedMaterial } from '~/redux/slices/material';
import {
  OptionalItemsState,
  selectSelectedOptionalItemsAsSelectedItemMap,
  selectWarrantyProduct,
  setCustomLaborOptionalItemsForProduct,
  setOptionalItemsMap,
} from '~/redux/slices/optionalItems';
import { selectZipcode } from '~/redux/slices/zipcode';

export interface OptionalProductContextValue {
  optionalProducts: OptionalProduct[];
  optionalProductsToProductDetails: () => ProductDetails[];
  getOptionalProductsProductQty: () => ProductQty[];
  restorableOptionalProducts: SelectedItemMap;
  setRestorableOptionalProducts: (value: SelectedItemMap) => void;
  makeRestorableOptionalProducts: () => SelectedItemMap;
  resetRestorableOptionalProducts: () => void;
  areOptionalProductsAvailable: () => boolean;
  makeOptionalProductNotes: () => ProductNote[];
}

export const [useOptionalProductContext, OptionalProductContext] =
  createTypeSafeContext<OptionalProductContextValue>();

export const OptionalProductContextProvider = ({
  children,
}: {
  children?: ReactNode;
}): JSX.Element => {
  const dispatch = useAppDispatch();

  const zipcode = useAppSelector(selectZipcode);
  const selectedStore = useSelectedStore();
  const selectedMaterial = useAppSelector(selectSelectedMaterial);
  const {
    getters: { areThereShapesInTheDrawingArea },
  } = useDrawingShapeContext();

  const { optionalItems: optionalProducts } = useOptionalItems();
  const selectedOptionalItems = useAppSelector(
    selectSelectedOptionalItemsAsSelectedItemMap
  );

  const [
    restorableOptionalProducts,
    setRestorableOptionalProducts,
    resetRestorableOptionalProducts,
  ] = useResetableState<Record<number, SelectedItemProperties>>({});

  // Ref to track if optional products have been restored for current estimate
  const hasRestoredOptionalProducts = useRef<boolean>(false);

  // Reset restoration flag when new restorable products are set (new estimate load)
  useEffect(() => {
    if (Object.keys(restorableOptionalProducts).length > 0) {
      hasRestoredOptionalProducts.current = false;
    }
  }, [restorableOptionalProducts]);

  const areOptionalProductsAvailable = () =>
    Boolean(
      zipcode &&
      selectedStore &&
      selectedMaterial &&
      areThereShapesInTheDrawingArea
    );

  const optionalProductsToProductDetails = (): ProductDetails[] =>
    optionalProducts.map<ProductDetails>((item) => ({
      productId: item.productId,
      name: item.name,
      consumerDesc: item.consumerDesc,
      // TODO: the 'optionalProduct' is being casted to comply with the 'GenericProduct' type, this will be worked separately when de-coupling the client and the server data layers
      productType: getEEProductType(item as unknown as GenericProduct),
      price: item.price,
      priceWithoutDiscount: item.price, // no discount in others
      promos: item.promos ? [...item.promos] : [],
      abbrevDesc: item.abbrevDesc,
      unitOfMeasure: item.unitOfMeasure,
      ...buildQteAttributes(item),
    }));

  const getOptionalProductsProductQty = (): ProductQty[] =>
    productQtyFromSelectedItems(selectedOptionalItems);

  const makeRestorableOptionalProducts = (): Record<
    number,
    SelectedItemProperties
  > =>
    Object.entries(selectedOptionalItems).reduce<
      Record<number, SelectedItemProperties>
    >((acc, [id, itemProps]) => {
      const parsedId = parseInt(id);

      if (itemProps.isSelected && itemProps.quantity > 0) {
        acc[parsedId] = itemProps;
      }

      return acc;
    }, {});

  const makeOptionalProductNotes = (): ProductNote[] =>
    optionalProducts.reduce<ProductNote[]>((productNotes, product) => {
      const selection = selectedOptionalItems[product.productId];

      if (selection && selection.isSelected && selection.note) {
        productNotes.push({
          productDescription: getProductDescription(product),
          note: selection.note,
        });
      }

      return productNotes;
    }, []);

  const restoreSelectedOptionalProducts = useCallback(() => {
    const selectedItemsMap: OptionalItemsState['selectedOptionalItems'] = {};
    const restoredSelection = optionalProducts.reduce(
      (acc, product) => {
        const restored = restorableOptionalProducts[product.productId];

        if (restored) {
          if (restored.detailEntries?.length) {
            dispatch(
              setCustomLaborOptionalItemsForProduct({
                productId: product.productId,
                customLaborOptionalItems: restored.detailEntries.map(
                  (detailEntry) => ({
                    notes: detailEntry.note,
                    productId: product.productId,
                    quantity: detailEntry.quantity,
                  })
                ),
              })
            );
          } else if (isWarrantyProduct(product)) {
            if (product.qteSubCode) {
              // handle warranty as a special case
              // TODO EE3-755 - all the selected warranty logic needs to be smarter, this is just a quick fix to restore properly
              dispatch(
                selectWarrantyProduct([product.qteSubCode, product.productId])
              );
            }
          } else {
            selectedItemsMap[product.productId] = restored.quantity;
          }
          acc[product.productId] = restored;
        }

        return acc;
      },
      {} as Record<number, SelectedItemProperties>
    );

    if (Object.keys(restoredSelection).length) {
      dispatch(setOptionalItemsMap(selectedItemsMap));
    }
    resetRestorableOptionalProducts();
  }, [
    optionalProducts,
    resetRestorableOptionalProducts,
    restorableOptionalProducts,
    dispatch,
  ]);

  useEffect(() => {
    if (
      optionalProducts.length &&
      Object.keys(restorableOptionalProducts).length &&
      !hasRestoredOptionalProducts.current
    ) {
      hasRestoredOptionalProducts.current = true;
      restoreSelectedOptionalProducts();
    }
  }, [
    optionalProducts,
    restorableOptionalProducts,
    restoreSelectedOptionalProducts,
  ]);

  return (
    <OptionalProductContext.Provider
      value={{
        optionalProducts,
        optionalProductsToProductDetails,
        getOptionalProductsProductQty,
        restorableOptionalProducts,
        setRestorableOptionalProducts,
        makeRestorableOptionalProducts,
        resetRestorableOptionalProducts,
        areOptionalProductsAvailable,
        makeOptionalProductNotes,
      }}
    >
      {children}
    </OptionalProductContext.Provider>
  );
};
